import React, {useCallback, useEffect, useRef, useState, useMemo} from "react";
import useBlotter from "./useBlotter";
import usePieGrid from "../../../SuperComponents/DataDisplay/PieGrid/usePieGrid";
import {useGptResponses} from "../../../hooks/useGptResponses";
import {useSnapshot} from "../../../Components/Snapshot/useSnapshot";
import useTickerMenu from "./useTickerMenu";
import {useSelector} from "react-redux";
import {selectResponseById} from "../../../Store/slices/gpt.slice";
import {percentReturn} from "../../../utils/math";
import {Col, Row, Space, Typography} from "antd";
import Snapshot from "../../../Components/Snapshot/Snapshot";
import BlotterToolbar from "./BlotterToolbar";
import StockChart from "../../../charts/StockChart";
import Button from "@mui/material/Button";
import Avatar from "@mui/material/Avatar";
import TimeseriesMenu from "./TimeseriesMenu";
import YearlyReturnsTable from "../../../SuperComponents/DataDisplay/YearlyReturnsTable";
import PieGrid from "../../../SuperComponents/DataDisplay/PieGrid/PieGrid";
import BlotterResponse from "./BlotterResponse";
import {formatDataForStockChart, handleRangeChange} from "./dataUtils";
import {useRelativeMetrics} from "../../../SuperComponents/StockCharts/useRelativeMetrics";
import GeneralStockChart from "../../../SuperComponents/StockCharts/GeneralStockChart";
import {selectTimeSeriesDataByPaths} from "../../../Store/slices/timeseries.slice";
import {findCommonTimestampsAndFilter} from "../../../utils/dataTransforms";
import {AddRounded} from "@mui/icons-material";
import {MultilineChartRounded, PollRounded} from "@mui/icons-material";
import {getDeltaSeries} from "./dataUtils";
import {rollingAverage, rollingSum} from "../../../utils/math";

function calculateWeightedRollingBeta(portfolioArray, betaSeriesArrays) {
  // Initialize the result array
  const weightedBetaSeries = [];

  // Iterate through each day in the portfolio
  for (let i = 0; i < portfolioArray.length; i++) {
    const snapshot = portfolioArray[i].snapshot;
    let weightedBetaSum = 0;
    let totalWeight = 0;

    // Calculate weighted beta for each ticker on this day
    for (const betaSeries of betaSeriesArrays) {
      const ticker = betaSeries.id;
      const weight = snapshot[ticker]?.wgt || 0;
      const beta = betaSeries.data[i];
      weightedBetaSum += weight * beta;
      totalWeight += weight;
    }
    weightedBetaSeries.push(weightedBetaSum > 0 ? (weightedBetaSum*100).toFixed(2) : 0);
  }

  return weightedBetaSeries;
}




function selectBetaPoints(selectedDates, timestamps, dataArray) {
    // Since the timestamps and selectedDates are the same, we can directly use the indices of selectedDates
    const selectedIndices = selectedDates.map(date => timestamps.indexOf(date));

    // Map over the dataArray to get the values at the selectedIndices
    return dataArray.map(dataItem => {
        return {
            id: dataItem.id,
            selectedData: selectedIndices.map(index => {
                return {
                    value: dataItem.data[index],
                    date: timestamps[index]
                }
            })
        };
    });
}



function reformatSelectedPointsToRows(portfolioBetas, tickerBetas) {
    const rows = {};

    // Process portfolio betas
    portfolioBetas.forEach(portfolioBeta => {
        portfolioBeta.selectedData.forEach(item => {
            const date = item.date;
            if (!rows[date]) {
                rows[date] = [];
            }
            rows[date].push({ id: portfolioBeta.id, value: item.value });
        });
    });

    // Process ticker betas
    tickerBetas.forEach(tickerBeta => {
        tickerBeta.selectedData.forEach(item => {
            const date = item.date;
            if (!rows[date]) {
                rows[date] = [];
            }
            rows[date].push({ id: tickerBeta.id, value: item.value });
        });
    });

    // Convert the rows object into an array of rows
    const formattedRows = Object.keys(rows).map(date => ({
        date,
        data: rows[date]
    }));

    return formattedRows;
}


const PortfolioChart = ({ portfolioTimeSeries, benchmarkTimeSeries, portfolio, categoryMapping, tickerLookup,
                        addTimeseriesSymbols, removeTimeseriesSymbols, extraTimeseries, allTickers }) => {
    const chartRef = useRef(null);
    const relativeMetrics = useRelativeMetrics({benchmark: portfolio?.benchmark});

    const [promptRows, setPromptRows] = useState(null);
    const [selectedTickers, setSelectedTickers] = useState([]);
    const [promptVisible, setPromptVisible] = useState(false);
    const [metricsChartVisible, setMetricsChartVisible] = useState(false);
    const [showMetricsChart, setShowMetricsChart] = useState(false);
    const [selectedBetaRows, setSelectedBetaRows] = useState(null);
    const [deltaSeries, setDeltaSeries] = useState(null);
    const { generatePrompt, stackSnapshots } = useBlotter();
    const { pieData, showPies, togglePies, updatePieData } = usePieGrid();
    const { responseId, submitPrompt } = useGptResponses();
    const { snapshotData, snapshotCallback, snapshotDate } = useSnapshot();
    const { onPlotHoldings } = useTickerMenu(portfolioTimeSeries, benchmarkTimeSeries, extraTimeseries, addTimeseriesSymbols, removeTimeseriesSymbols);

    const selectedPoints = useSelector(state => state.appStates.selectedChartPoints);
    const gptResponse = useSelector(state => selectResponseById(state, responseId));




    const fullTickerSet = useMemo(()=>{
        return [{id: portfolio.id, name: portfolio.name}, ...allTickers.map(x=>{return {id: x.symbol, name: x.name}})];
    }, [allTickers]);

    const {data: betaSeries } = useSelector(state=> {
        const paths = allTickers.map(item => {
                          return {
                            path: `timeSeries.metrics.${item.symbol}.${relativeMetrics.metric}.${relativeMetrics.targetSymbol}.${relativeMetrics.window}`,
                            id: item.symbol
                          }
        });
        return selectTimeSeriesDataByPaths(state, paths);
    });


    const callbackOnRangeChange = useCallback((start, end) => {
        handleRangeChange(start, end, portfolioTimeSeries, updatePieData, togglePies);
    }, [portfolioTimeSeries, updatePieData, togglePies]);


    const handleDeltaSeries = useCallback(()=>{
        const deltaSeries = getDeltaSeries(portfolioTimeSeries);
        setDeltaSeries(deltaSeries);
    })

    useEffect(()=>{
        if (selectedPoints.length===2){
            const [x1, x2] = [selectedPoints[0].timestamp, selectedPoints[1].timestamp].sort((a, b)=>a.localeCompare(b));
            const [y1, y2] = [selectedPoints[0].items[0].data[1], selectedPoints[1].items[0].data[1]];
            const y0 = portfolioTimeSeries[0].close;
            chartRef.current.drawAnnotation().drawLineSegment({x1, x2, y1: percentReturn(y1, y0)*100, y2: percentReturn(y2, y0)*100});
            chartRef.current.finishAnnotation();
            // selectRange(timestamps[0], timestamps[1]);
        }
    }, [selectedPoints]);

    const dataSeries = formatDataForStockChart(portfolioTimeSeries, benchmarkTimeSeries, extraTimeseries, snapshotCallback, portfolio);



    const fullFilteredBetaAndTimeseries = betaSeries  && findCommonTimestampsAndFilter([
        portfolioTimeSeries, ...betaSeries.map(x=>x.data)
    ]);
    const labeledBetaSeries = fullFilteredBetaAndTimeseries && fullFilteredBetaAndTimeseries.slice(1, ).map((x, i)=>{
        return {id: betaSeries[i].timeseriesId, data: x.map(x=>x.value)}
    });
    const portfolioSeriesToBeta = fullFilteredBetaAndTimeseries && fullFilteredBetaAndTimeseries[0];
    const weightedRollingBetaSeries = portfolioSeriesToBeta && calculateWeightedRollingBeta(portfolioSeriesToBeta, labeledBetaSeries);
    const submitGptPrompt = useCallback(()=>{
        const selectedDates = selectedPoints.map(x=>x.timestamp);
        const selectedBetas = selectBetaPoints(selectedDates, portfolioSeriesToBeta.map(x=>x.timestamp), labeledBetaSeries);
        const portfolioBetas = selectBetaPoints(selectedDates, portfolioSeriesToBeta.map(x=>x.timestamp), [{
            id: 'portfolio', data: weightedRollingBetaSeries.map(x=>parseFloat(x)*0.01)
        }]);
        const snapshots = stackSnapshots(selectedPoints);
        const stackedBetas = reformatSelectedPointsToRows(portfolioBetas, selectedBetas);
        setSelectedBetaRows(stackedBetas)
        const {promptString, rows} = generatePrompt(snapshots, extraTimeseries && extraTimeseries.map(x=>x.symbol), stackedBetas);
        submitPrompt(JSON.stringify(promptString), `Activity Insight: ${portfolio.name}`, '');
        setPromptRows(rows);

    }, [submitPrompt, selectedPoints, extraTimeseries]);






    const handleMetricsPlugin = ()=>{
        relativeMetrics.fetchNewData(fullTickerSet.map(x=>x.id), ()=>setMetricsChartVisible(true), portfolioTimeSeries?.[0]?.timestamp);
    }



    let chartDataSeriesConfig = [
                        {
                            plot: 0,
                            data: dataSeries,
                            yScale: 'percent',
                            yLabelPostfix: '%',
                            stroke: '#0097fb',
                            legend: {
                                format: "%",
                                precision: 2
                            },
                            height: 400,
                        },

    ];
    if (deltaSeries){
        const sumValues = rollingSum(deltaSeries.map(x=>x[1]), 5);
        const sumSeries = [...deltaSeries].slice(5, ).map((x, i)=>[x[0], sumValues[i]]);
        chartDataSeriesConfig.push({
            plot: 1,
            data: [
                {
                    name: 'Daily Change',
                    data: deltaSeries
                },
                {
                    name: '5 Day Change',
                    data: sumSeries,
                    stroke: '#f36581'
                }
            ],
            height: 200,
            xGrid: false,
            yGrid: false,
        });
    }
    if (weightedRollingBetaSeries && betaSeries.length > 0){
        const alignedTimestamps = portfolioSeriesToBeta.map(x=>x.timestamp);
        chartDataSeriesConfig.push({
            plot: chartDataSeriesConfig.length,
            data: [
                {
                    name: `Portfolio Composite Beta`,
                    data: alignedTimestamps.map(x=>[x, weightedRollingBetaSeries[alignedTimestamps.indexOf(x)]])
                },
                {
                    name: `${relativeMetrics.targetSymbol?.toUpperCase() ?? 'Benchmark'} Benchmark`,
                    data: alignedTimestamps.map(x=>[x, 100]),
                    stroke: '#f36581'
                }
            ],
            yScale: 'linear',
            height: 300,
            xGrid: false,
            yGrid: false,
        });
    }

    return promptVisible === false || !gptResponse ? (
        <Row  gutter={[32, 0]}>
            <Col flex={'0 0 300px'}>
                <Snapshot snapshot={snapshotData} categoryMapping={categoryMapping} colorLookup={tickerLookup} date={snapshotDate}/>
            </Col>
            <Col flex={'1 1'}>
                {selectedPoints.length > 0 && (
                    <div style={{marginBottom: '10px'}}>
                        <BlotterToolbar selectedPoints={selectedPoints}
                                        chartRef={chartRef}
                                        submitPrompt={submitGptPrompt}
                        />
                    </div>
                )}
                <StockChart
                    height={600}
                    ref={chartRef}
                    dataSeries={chartDataSeriesConfig}
                    customSnapshotCallback={snapshotCallback}
                    callbackOnRangeChange={callbackOnRangeChange}
                    extraToolsLeft={
                    <Space>
                        {gptResponse && (
                            <Button onClick={()=>setPromptVisible(true)}>
                                <Avatar sx={{height: 48, width: 48}} src={'https://storage.googleapis.com/finiat-web-static-resources/chartIcons/gpt-3d.webp'}/>
                            </Button>
                        )}
                        {!deltaSeries && (
                            <Button startIcon={<PollRounded/>} size={'small'} variant={'text'} color={'primary'} onClick={handleDeltaSeries}>
                                Show Change
                            </Button>
                        )}
                        {!metricsChartVisible && (
                            <Button startIcon={<MultilineChartRounded/>} size={'small'} variant={'text'} color={'primary'} onClick={handleMetricsPlugin}>
                                Show Metrics
                            </Button>
                        )}
                        <TimeseriesMenu
                            onPlotHoldings={onPlotHoldings}
                            allTickers={allTickers}
                            selectedTickers={selectedTickers}
                            setSelectedTickers={setSelectedTickers}
                            currentTimeseries={Array.from(new Set([...portfolioTimeSeries, ...benchmarkTimeSeries, ...extraTimeseries].map(series => series.symbol))).length}
                        />
                    </Space>
                    }
                />

                {metricsChartVisible && showMetricsChart ? (
                    <>
                        <Typography.Title style={{marginLeft:'60px', padding: '30px 0'}} level={3}>
                            {relativeMetrics.targetSymbol.toUpperCase()}
                            <Typography.Text strong style={{paddingLeft: '10px'}}>Benchmark ticker used as market series for beta calculations.</Typography.Text>
                        </Typography.Title>
                        <GeneralStockChart
                          dataPaths={
                            fullTickerSet.map(item => {
                              return {
                                path: `timeSeries.metrics.${item.id}.${relativeMetrics.metric}.${relativeMetrics.targetSymbol}.${relativeMetrics.window}`,
                                id: item.id
                              }
                            })
                          }
                          seriesConfig={fullTickerSet.reduce((config, item) => {
                            config[item.id] = { dataField: 'value', yScale: 'linear' };
                            return config;
                          }, {})}
                          labelMapping={fullTickerSet.reduce((mapping, item) => {
                            mapping[item.id] = item.name;
                            return mapping;
                          }, {})}
                          stockChartProps={{
                            allowChartInteractions: true,
                          }}
                        />
                    </>
                ) : <Button onClick={()=>setShowMetricsChart(true)}>Show Composite Betas</Button>}

                {!metricsChartVisible && !showPies && (
                    <div style={{padding: '20px'}}>
                        <YearlyReturnsTable
                            seriesData={[
                                { name: portfolio?.name, data: portfolioTimeSeries },
                                { name: 'Benchmark', data: benchmarkTimeSeries }
                            ]}
                        />
                    </div>
                )}
                {!metricsChartVisible && showPies && (
                    <PieGrid
                        chartData={pieData.data}
                        colorMapping={categoryMapping}
                        fieldNames={pieData.fieldNames}
                        rows={16}
                    />

                )}
            </Col>
        </Row>

        ) : <BlotterResponse response={gptResponse} data={promptRows} closeResponse={()=>setPromptVisible(false)}
                             extraDetails={{
                                 betaRows: selectedBetaRows
                             }}
    />
};



export default PortfolioChart;